import { useState } from 'react'
import { MultiAssigneePicker } from './MultiAssigneePicker'
import type { EventAction, ActionPriority, ActionStatus } from '../types'

// ActionEditor — create / edit modal for an EventAction. Extracted from
// ActionsTab.tsx so it can also be hosted from /my-actions (and any
// future surface that wants a popup edit of an action).
//
// The popup is purely a controlled form — the parent owns the
// save/error/saving lifecycle via `onSave`, `serverError`, `saving`
// props. That way ActionsTab can wire it to useCreateAction /
// usePatchAction with its own cache invalidation, while MyActions
// can use usePatchAction directly with an additional invalidate of
// the ['my-actions'] query key.

// Charlotte Hawes (dbo.Users.id = 2) — project-wide default assignee
// for new Actions. Edit mode preserves whatever the row already has.
const DEFAULT_ASSIGNEE_USER_ID = 2

export interface ActionEditorBody {
  title: string
  description: string | null
  department: string | null
  assigneeIds: number[]
  dueAt: string | null
  priority: ActionPriority
  status: ActionStatus
  // Only sent on create (default true → spawns a Gantt task). Existing actions
  // toggle plan membership via the row pill, so edit never sends this.
  addToPlan?: boolean
}

interface Props {
  mode: 'create' | 'edit'
  initial: EventAction | null
  saving: boolean
  serverError: Error | null
  onClose: () => void
  onSave: (body: ActionEditorBody) => void
}

export function ActionEditor({
  mode, initial, saving, serverError, onClose, onSave
}: Props) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [department, setDepartment] = useState(initial?.department ?? '')
  const [dueAt, setDueAt] = useState(initial?.dueAt ?? '')
  const [priority, setPriority] = useState<ActionPriority>(initial?.priority ?? 'medium')
  const [status, setStatus] = useState<ActionStatus>(initial?.status ?? 'open')
  const [assigneeIds, setAssigneeIds] = useState<number[]>(
    mode === 'edit' ? (initial?.assigneeIds ?? []) : [DEFAULT_ASSIGNEE_USER_ID]
  )
  // New actions default to on-plan (item 11). Existing actions toggle via the
  // row pill, so this control only shows on create.
  const [addToPlan, setAddToPlan] = useState(true)

  const isValid = title.trim().length > 0

  function submit() {
    if (!isValid) return
    onSave({
      title: title.trim(),
      description: description.trim() || null,
      department: department.trim() || null,
      assigneeIds,
      dueAt: dueAt || null,
      priority,
      status,
      ...(mode === 'create' ? { addToPlan } : {})
    })
  }

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        className="bg-white rounded-lg shadow-xl w-full max-w-xl my-8 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">
            {mode === 'create' ? 'New action' : 'Edit action'}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink text-xl">×</button>
        </div>

        <div className="space-y-3">
          <Label text="Title">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              autoFocus
              required
              className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
            />
          </Label>

          <Label text="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
            />
          </Label>

          <div className="grid grid-cols-2 gap-3">
            <Label text="Department">
              <input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                maxLength={80}
                placeholder="e.g. Operations"
                className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
              />
            </Label>
            <Label text="Due date">
              <input
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
              />
            </Label>
          </div>

          <MultiAssigneePicker
            value={assigneeIds}
            onChange={setAssigneeIds}
            disabled={saving}
          />

          <div className="grid grid-cols-2 gap-3">
            <Label text="Priority">
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as ActionPriority)}
                className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Label>
            <Label text="Status">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ActionStatus)}
                className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green"
              >
                <option value="open">Open</option>
                <option value="in-progress">In progress</option>
                <option value="done">Done</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Label>
          </div>

          {mode === 'create' && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={addToPlan}
                onChange={(e) => setAddToPlan(e.target.checked)}
                className="w-4 h-4 accent-green"
              />
              <span>Add to the project plan <span className="text-ink-soft">(creates a Gantt task)</span></span>
            </label>
          )}

          {serverError && (
            <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
              {serverError.message}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-line">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || saving}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Add action' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Label({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
        {text}
      </span>
      {children}
    </label>
  )
}
